/* Gift Card Section behavior
   Handles: copy-to-clipboard for the gift code, variant/price syncing,
   and AJAX add-to-cart for the gift card product.
   Safe to run multiple times on a page (each instance is scoped to its own root). */

(function () {
  "use strict";

  function formatMoney(cents, format) {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    cents = parseInt(cents, 10) || 0;

    function defaultTo(value, fallback) {
      return typeof value === "undefined" || value === null ? fallback : value;
    }

    function withDelimiters(number, precision, thousands, decimalSep) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ",");
      decimalSep = defaultTo(decimalSep, ".");
      var value = (number / 100).toFixed(precision);
      var parts = value.split(".");
      var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1" + thousands);
      var centsPart = parts[1] ? decimalSep + parts[1] : "";
      return dollars + centsPart;
    }

    var formatString = format || "\u0024{{amount}}";
    var match = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) {
      return formatString;
    }

    var value;
    switch (match[1]) {
      case "amount_no_decimals":
        value = withDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = withDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = withDelimiters(cents, 0, ".", ",");
        break;
      default:
        value = withDelimiters(cents, 2);
    }
    return formatString.replace(match[0], value);
  }

  function fallbackCopy(text) {
    var textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand("copy");
    } catch (err) {
      /* no-op: clipboard not available */
    }
    document.body.removeChild(textarea);
  }

  function copyText(text) {
    if (!text) {
      return Promise.resolve();
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).catch(function () {
        fallbackCopy(text);
      });
    }
    fallbackCopy(text);
    return Promise.resolve();
  }

  function initCopy(root) {
    var codeTextEl = root.querySelector("[data-gcs-code-text]");
    var triggers = root.querySelectorAll("[data-gcs-copy-trigger]");

    triggers.forEach(function (trigger) {
      trigger.addEventListener("click", function () {
        var text = codeTextEl ? codeTextEl.textContent.trim() : "";
        copyText(text).then(function () {
          trigger.classList.add("is-copied");

          var label = trigger.querySelector("[data-gcs-copy-label]");
          if (label) {
            var original = label.getAttribute("data-original-text") || label.textContent;
            label.setAttribute("data-original-text", original);
            label.textContent = trigger.getAttribute("data-copied-label") || "Copied!";
          }

          window.clearTimeout(trigger._gcsCopyTimeout);
          trigger._gcsCopyTimeout = window.setTimeout(function () {
            trigger.classList.remove("is-copied");
            if (label) {
              label.textContent = label.getAttribute("data-original-text");
            }
          }, 2200);
        });
      });
    });
  }

  function initProductForm(root) {
    var form = root.querySelector("[data-gcs-product-form]");
    if (!form) {
      return;
    }

    var variantSelect = form.querySelector("[data-gcs-variant-select]");
    var variantDataEl = form.querySelector("[data-gcs-variant-data]");
    var priceEl = form.querySelector("[data-gcs-price]");
    var comparePriceEl = form.querySelector("[data-gcs-compare-price]");
    var addToCartBtn = form.querySelector("[data-gcs-add-to-cart]");
    var qtyInput = form.querySelector("[data-gcs-qty]");
    var messageEl = form.querySelector("[data-gcs-message]");
    var moneyFormat = root.getAttribute("data-money-format");
    var cartAddUrl = root.getAttribute("data-cart-add-url");
    var cartUrl = root.getAttribute("data-cart-url");

    var variants = [];
    if (variantDataEl) {
      try {
        variants = JSON.parse(variantDataEl.textContent);
      } catch (err) {
        variants = [];
      }
    }

    function getVariantById(id) {
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(id)) {
          return variants[i];
        }
      }
      return null;
    }

    function updateForVariant(variant) {
      if (!variant || !addToCartBtn) {
        return;
      }
      addToCartBtn.setAttribute("data-variant-id", variant.id);
      var textEl = addToCartBtn.querySelector("[data-gcs-btn-text]");

      if (variant.available) {
        addToCartBtn.disabled = false;
        if (textEl) {
          textEl.textContent = addToCartBtn.getAttribute("data-label-available");
        }
      } else {
        addToCartBtn.disabled = true;
        if (textEl) {
          textEl.textContent = addToCartBtn.getAttribute("data-label-soldout");
        }
      }

      if (priceEl) {
        priceEl.textContent = formatMoney(variant.price, moneyFormat);
      }

      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = formatMoney(variant.compare_at_price, moneyFormat);
          comparePriceEl.style.display = "";
        } else {
          comparePriceEl.style.display = "none";
        }
      }
    }

    if (variantSelect) {
      variantSelect.addEventListener("change", function () {
        updateForVariant(getVariantById(this.value));
      });
    }

    if (addToCartBtn) {
      addToCartBtn.addEventListener("click", function (event) {
        event.preventDefault();
        if (addToCartBtn.disabled || !cartAddUrl) {
          return;
        }

        var variantId = variantSelect ? variantSelect.value : addToCartBtn.getAttribute("data-variant-id");
        var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

        addToCartBtn.classList.add("is-loading");
        addToCartBtn.disabled = true;
        if (messageEl) {
          messageEl.textContent = "";
        }

        fetch(cartAddUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] })
        })
          .then(function (response) {
            return response.json().then(function (data) {
              return { ok: response.ok, data: data };
            });
          })
          .then(function (result) {
            if (!result.ok) {
              var errorMessage =
                (result.data && (result.data.description || result.data.message)) ||
                "That gift card could not be added to your cart.";
              throw new Error(errorMessage);
            }
            document.dispatchEvent(new CustomEvent("cart:updated", { detail: result.data }));
            if (cartUrl) {
              window.location.href = cartUrl;
            } else {
              addToCartBtn.classList.remove("is-loading");
              addToCartBtn.disabled = false;
            }
          })
          .catch(function (err) {
            addToCartBtn.classList.remove("is-loading");
            addToCartBtn.disabled = false;
            if (messageEl) {
              messageEl.textContent = err.message;
            }
          });
      });
    }
  }

  function initScrollLinks(root) {
    var scrollButtons = root.querySelectorAll("[data-gcs-scroll-to]");
    scrollButtons.forEach(function (btn) {
      btn.addEventListener("click", function (event) {
        var selector = btn.getAttribute("data-gcs-scroll-to");
        var target = selector ? document.querySelector(selector) : null;
        if (target) {
          event.preventDefault();
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    });
  }

  function initGiftCardSection(root) {
    if (!root || root.getAttribute("data-gcs-initialized") === "true") {
      return;
    }
    root.setAttribute("data-gcs-initialized", "true");
    initCopy(root);
    initProductForm(root);
    initScrollLinks(root);
  }

  function initAll(scope) {
    var context = scope || document;
    context.querySelectorAll("[data-gcs-root]").forEach(initGiftCardSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* Re-init when a merchant edits section settings in the theme editor,
     since Shopify re-renders the section markup via AJAX. */
  document.addEventListener("shopify:section:load", function (event) {
    var root = event.target.querySelector("[data-gcs-root]");
    if (root) {
      root.removeAttribute("data-gcs-initialized");
      initGiftCardSection(root);
    }
  });
})();